import { useState, useEffect, useRef } from "react";
import { Button, Input, Col, Row } from "reactstrap";
import { API_BASE_URL } from "../../constants/api.constants";
import axios from "../../services/api/api";
import FoundUserInfo from "../UserManagement/FoundUserInfo";

export default function FindUserInfo() {
    const [foundUser, setFoundUser] = useState({});
    const [email, setEmail] = useState("");
    const [emails, setEmails] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);

    // refs till varje förslagsrad
    const suggestionRefs = useRef([]);

    // load the email list one time
    useEffect(() => {
        const loadEmails = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/User/Emails`);
                const list = Array.isArray(res.data?.result) ? res.data.result : [];
                setEmails(list || []);
            } catch (err) {
                console.error("Kunde inte hämta emails", err);
            }
        };
        loadEmails();
    }, []);

    // scrolla aktiv rad in i vy när highlightIndex ändras
    useEffect(() => {
        if (highlightIndex < 0 || highlightIndex >= suggestions.length) return;
        const el = suggestionRefs.current[highlightIndex];
        if (el) {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightIndex, suggestions.length]);

    // sök-funktion – kan ta ett email direkt (t.ex. valt förslag)
    const searchUser = (emailOverride) => {
        const userEmail = (emailOverride ?? email).trim();
        if (!userEmail) return;

        axios
            .get(
                `${API_BASE_URL}/User/ByEmail?email=${encodeURIComponent(
                    userEmail
                )}`
            )
            .then((response) => {
                var res = response?.data?.result;

                if (res) {
                    setFoundUser({
                        UserFound: true,
                        Id: res.id,
                        IsDeleted: res.isDeleted,
                        Created: res.created,
                        Name: res.name,
                        Email: res.email,
                        Type: res.userType,
                        Activated: res.hasActivatedAccount,
                        HasFullTeacherAccess: res.hasFullTeacherAccess,
                        LicenceOnSchool: res.licenceOnSchool,
                        Schools: res.schools,
                        Classes: res.classes,
                        Licenses: res.coursesInLicense,
                        ProgramLicenses: res.licenses,
                        IsPurchaser: res.isPurchaser,
                        AplLicenseValidTo: res.aplLicenseValidTo,
                        AplAppInfo: res.aplAppInfo,
                        ClassIsDeleted: res.classIsDeleted
                    });
                }
            })
            .catch((err) => {
                var failMessage = err?.response?.data
                    ? err.response.data
                    : err;
                setFoundUser({ UserFound: false, failMessage: failMessage });
            });
    };

    // handle typing + suggestions
    const onEmailChange = (e) => {
        const value = e.target.value.toLowerCase();
        setEmail(value);
        setHighlightIndex(-1); // reset markering när man skriver

        if (!value || value.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const matched = emails.filter(x => x.includes(value)).slice(0, 50);
        setSuggestions(matched);
        setShowSuggestions(matched.length > 0);
    };

    // when a suggestion is clicked
    const onSelectSuggestion = (value) => {
        setEmail(value);
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightIndex(-1);
    };

    const CheckUser = () => {
        if (foundUser.UserFound === false) {
            return <FoundUserInfo foundUser={foundUser} />;
        } else if (foundUser.UserFound === true) {
            return <FoundUserInfo foundUser={foundUser} />;
        } else {
            return null;
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            if (!showSuggestions || suggestions.length === 0) return;
            e.preventDefault();
            setHighlightIndex((prev) =>
                prev + 1 >= suggestions.length ? 0 : prev + 1
            );
            return;
        }

        if (e.key === "ArrowUp") {
            if (!showSuggestions || suggestions.length === 0) return;
            e.preventDefault();
            setHighlightIndex((prev) =>
                prev - 1 < 0 ? suggestions.length - 1 : prev - 1
            );
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();

            // 1) om ett förslag är markerat → sök på det, EN gång
            if (
                showSuggestions &&
                suggestions.length > 0 &&
                highlightIndex >= 0 &&
                highlightIndex < suggestions.length
            ) {
                const selected = suggestions[highlightIndex];
                setEmail(selected);
                setSuggestions([]);
                setShowSuggestions(false);
                setHighlightIndex(-1);

                // sök på det valda värdet (skicka in direkt → ingen state-racing)
                searchUser(selected);
                return;
            }

            // 2) ingen markering → sök på det som står i fältet
            if (email.trim() !== "") {
                searchUser();
            }
            return;
        }

        if (e.key === "Escape") {
            setShowSuggestions(false);
            setHighlightIndex(-1);
        }
    };

    return (
        <div className="container-fluid admin-container">
            <Row>
                <Col className="col-3 mb-2">
                    <h5 className="no-margin">Sök användare</h5>
                </Col>
            </Row>

            <Row className="search-row">
                <Col className="col-3" style={{ position: "relative" }}>
                    <Input
                        type="text"
                        id="user-search-email"
                        placeholder="email"
                        value={email}
                        onChange={onEmailChange}
                        onKeyDown={onKeyDown}
                        autoComplete="new-password"
                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
                    />

                    {showSuggestions && suggestions.length > 0 && (
                        <div
                            style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                background: "white",
                                border: "1px solid #ccc",
                                zIndex: 15,
                                maxHeight: "200px",
                                overflowY: "auto"
                            }}
                        >
                            {suggestions.map((s, i) => (
                                <div
                                    key={s}
                                    ref={(el) => (suggestionRefs.current[i] = el)}
                                    onMouseDown={() => onSelectSuggestion(s)}
                                    style={{
                                        padding: "6px 8px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #eee",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        background: i === highlightIndex ? "#eef6ff" : "white"
                                    }}
                                >
                                    {s}
                                </div>
                            ))}
                        </div>
                    )}
                </Col>
            </Row>

            <Row>
                <Col className="col-3 mt-2">
                    <Button
                        color="primary"
                        outline
                        onClick={() => searchUser()}
                    >
                        Sök
                    </Button>
                </Col>
            </Row>

            {CheckUser()}
        </div>
    );
}
